import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const getVisiblePages = (currentPage: number, totalPages: number) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const windowSize = 5;
  const halfWindow = Math.floor(windowSize / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + windowSize - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = endPage - windowSize + 1;
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
};

const buildPaginationItems = (currentPage: number, totalPages: number) => {
  const visiblePages = getVisiblePages(currentPage, totalPages);
  const items: Array<number | 'ellipsis-start' | 'ellipsis-end'> = [];

  if (visiblePages[0] > 1) {
    items.push(1);
  }

  if (visiblePages[0] > 2) {
    items.push('ellipsis-start');
  }

  items.push(...visiblePages);

  const lastVisiblePage = visiblePages[visiblePages.length - 1];
  if (lastVisiblePage < totalPages - 1) {
    items.push('ellipsis-end');
  }

  if (lastVisiblePage < totalPages) {
    items.push(totalPages);
  }

  return items;
};

export function useCatalogPagination(totalPages?: number) {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        if (page <= 1) {
          nextParams.delete('page');
        } else {
          nextParams.set('page', String(page));
        }
        return nextParams;
      }, { replace: true });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setSearchParams]
  );

  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages ?? 1),
    [currentPage, totalPages]
  );

  return {
    currentPage,
    paginationItems,
    handlePageChange,
  };
}
